import { useTranslation } from '../../i18n/useTranslation';
import styles from './orders-details-inside.module.scss';
import globalStyles from '../../global-styles.module.scss';
import classNames from 'classnames';
import {
    getExcelOrderSummary,
    getExistOrderAsShoppingCart,
    getPdfOrderSummary,
    getPriceWithPrecision,
} from '../../components/shopping-cart/services/shopping-cart.service';
import { htmlId } from './orders-details-inside.service';
import { getCurrencySymbol } from '../../components/order/services/currency.service';
import { DocumentFileContent } from '../../shared/models/document-file-content.model';
import { getAndShowFileDocument, getFileByUuid } from '../../shared/services/attachments.service';
import { pdfFileNameOrderSummary } from '../shopping-cart-container/order-confirmation/order-confirmation.service';
import { FileDocumentTypeCode } from '../../shared/models/file-document-type.enum';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { IShopingCart } from '../../components/order/interfaces/shopping-cart.interface';
import { IBuildingSiteListViewDto } from '../../components/order/interfaces/building-site-list-view.interface';
import { IShopingCartProduct } from '../../components/order/interfaces/shopping-cart-product.interface';
import RotatingRing from '../../components/rotating-ring-animation/RotatingRing';
import React from 'react';
import { AttachmentFile } from '../../shared/models/attachment-file.model';

export interface IOrdersDetailsInsideInput {
    documentId: string;
    documentNumber: string;
    documentDate: Date;
    documentStatusName: string | null;
    orderBondFileUuid: string | null;
}

const OrdersDetailsInside: React.FC<IOrdersDetailsInsideInput> = ({
    documentId,
    documentNumber,
    documentDate,
    documentStatusName,
    orderBondFileUuid,
}) => {
    const { t } = useTranslation('translation');
    const [buildingSite, setBuildingSite] = useState<IBuildingSiteListViewDto>();
    const [shoppingCart, setShoppingCart] = useState<IShopingCart>({
        products: [] as IShopingCartProduct[],
    } as IShopingCart);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const showError = (message: string) => toast.error(message);

    const getTranslate = (code: string) => {
        return t('FIXCENTER_ORDERING.' + code);
    };

    const handleGetPdfOrderSummary = () => {
        setIsLoading(true);
        getPdfOrderSummary(documentId, documentNumber, documentDate, documentStatusName ? documentStatusName : '-')
            .then((response) => {
                if (response) {
                    const model: DocumentFileContent = response.data;
                    if (!model.message) {
                        getAndShowFileDocument(model.file, pdfFileNameOrderSummary, FileDocumentTypeCode.PDF);
                        setIsLoading(false);
                    } else {
                        showError(getTranslate('MESSAGE_9'));
                        setIsLoading(false);
                    }
                } else {
                    showError(getTranslate('MESSAGE_9'));
                    setIsLoading(false);
                }
            })
            .catch(() => {
                showError(getTranslate('MESSAGE_9'));
                setIsLoading(false);
            });
    };

    const handleGetExcelOrderSummary = () => {
        setIsLoading(true);
        getExcelOrderSummary(documentId, documentNumber, documentDate, documentStatusName ? documentStatusName : '-')
            .then((response) => {
                if (response) {
                    const model: DocumentFileContent = response.data;
                    if (!model.message) {
                        getAndShowFileDocument(model.file, documentNumber, FileDocumentTypeCode.EXCEL, false);
                        setIsLoading(false);
                    } else {
                        showError(getTranslate('MESSAGE_9'));
                        setIsLoading(false);
                    }
                } else {
                    showError(getTranslate('MESSAGE_9'));
                    setIsLoading(false);
                }
            })
            .catch(() => {
                showError(getTranslate('MESSAGE_9'));
                setIsLoading(false);
            });
    };

    const handleGetOrderBondFile = () => {
        setIsLoading(true);
        getFileByUuid(orderBondFileUuid as string)
            .then((response) => {
                if (response) {
                    const model: AttachmentFile = response.data;
                    if (model) {
                        getAndShowFileDocument(model.content, model.name, FileDocumentTypeCode.PDF);
                        setIsLoading(false);
                    } else {
                        showError(getTranslate('MESSAGE_9'));
                        setIsLoading(false);
                    }
                } else {
                    showError(getTranslate('MESSAGE_9'));
                    setIsLoading(false);
                }
            })
            .catch(() => {
                showError(getTranslate('MESSAGE_9'));
                setIsLoading(false);
            });
    };

    const deleteOrderBondFile = () => {};

    const handleReOpenOrderInShoppingCart = () => {
        setIsLoading(true);
        getExistOrderAsShoppingCart(documentId).then((response) => {
            if (response) {
                const model: IShopingCart = response.data;
                setShoppingCart(model);
                setBuildingSite(model.buildingSite);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        });
    };

    useEffect(() => {
        handleReOpenOrderInShoppingCart();
    }, []);
    return (
        <>
            <div className={styles.container}>
                {isLoading && <RotatingRing />}
                <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('STATUS')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>{documentStatusName}</label>
                    </div>
                </div>
                <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('BUILDING_SITE')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>
                            {buildingSite && buildingSite.code + ' | ' + buildingSite.name}
                        </label>
                    </div>
                </div>
                <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('REFERENCE')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>{shoppingCart.detail?.reference}</label>
                    </div>
                </div>

                <section className={classNames(globalStyles.float_right_item, globalStyles.mb_3)}>
                    <button
                        id={`${htmlId}-export-to-excel-btn`}
                        className={classNames(globalStyles.defult_btn, globalStyles.mr_2, styles.button)}
                        onClick={() => {
                            handleGetExcelOrderSummary();
                        }}
                    >
                        {t('Excel')}
                    </button>
                    <button
                        id={`${htmlId}-export-to-pdf-btn`}
                        className={classNames(globalStyles.defult_btn, globalStyles.mr_2, styles.button)}
                        onClick={() => {
                            handleGetPdfOrderSummary();
                        }}
                    >
                        {t('Pdf')}
                    </button>
                    {orderBondFileUuid && (
                        <React.Fragment>
                            <button
                                id={`${htmlId}-download-bond-btn`}
                                className={classNames(globalStyles.defult_btn, globalStyles.mr_2, styles.button)}
                                onClick={() => {
                                    handleGetOrderBondFile();
                                }}
                            >
                                {t('DOWNLOAD_BOND')}
                            </button>
                            {/* <button
                            id={`${htmlId}-delete-bond-btn`}
                            className={classNames(globalStyles.defult_btn, globalStyles.mr_2, styles.button)}
                            onClick={() => {deleteOrderBondFile()}}>
                            {t('DELETE_BOND')}
                        </button> */}
                        </React.Fragment>
                    )}
                </section>

                <div className={styles.order_header}>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('VARIANT_NAME')}</label>
                    </div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('UNIT_PRICE')}</label>
                    </div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('QUANTITY')}</label>
                    </div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('NET_PRICE')}</label>
                    </div>
                </div>
                <div className={styles.order_content}>
                    {shoppingCart &&
                        shoppingCart.products &&
                        shoppingCart.products.map((item, index) => (
                            <div
                                className={`${classNames(styles.row_content, globalStyles.w_100)} ${index % 2 !== 0 ? styles.shading_row : ''}`}
                                key={index}
                            >
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3, globalStyles.mr_3)}>
                                        {item.productName}
                                    </label>
                                </div>
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3, globalStyles.mr_1)}>{item.netto}</label>
                                    <label className={classNames(styles.h3)}>
                                        {getCurrencySymbol(item.saleCurrencyName)}
                                    </label>
                                </div>
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3)}>{item.quantity}</label>
                                </div>
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3, globalStyles.mr_1)}>
                                        {item.nettoTotalPrice}
                                    </label>
                                    <label className={classNames(styles.h3)}>
                                        {getCurrencySymbol(item.saleCurrencyName)}
                                    </label>
                                </div>
                            </div>
                        ))}
                </div>
                <div
                    className={`${classNames(styles.row_content, globalStyles.w_100)} ${shoppingCart.products.length % 2 !== 0 ? styles.shading_row : ''}`}
                >
                    <div className={styles.column}>
                        <label className={classNames(styles.h3)}>{t('SHIPPING_COST')}</label>
                    </div>
                    <div className={styles.column}></div>
                    <div className={styles.column}></div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h3, globalStyles.mr_1)}>
                            {getPriceWithPrecision(shoppingCart.shippingCost)}
                        </label>
                        <label className={classNames(styles.h3)}>
                            {Array.isArray(shoppingCart.products) && shoppingCart.products.length > 0
                                ? getCurrencySymbol(shoppingCart.products[0].saleCurrencyName)
                                : '_'}
                        </label>
                    </div>
                </div>
                <div className={classNames(styles.row_content, globalStyles.w_100)}>
                    <div className={styles.column}>
                        <label className={classNames(styles.h4)}>{t('TOTAL')}</label>
                    </div>
                    <div className={styles.column}></div>
                    <div className={styles.column}></div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h3, globalStyles.mr_1)}>
                            {getPriceWithPrecision(shoppingCart.nettoTotalPrice)}
                        </label>
                        <label className={classNames(styles.h3)}>
                            {Array.isArray(shoppingCart.products) && shoppingCart.products.length > 0
                                ? getCurrencySymbol(shoppingCart.products[0].saleCurrencyName)
                                : '_'}
                        </label>
                    </div>
                </div>
            </div>
        </>
    );
};

export default OrdersDetailsInside;
